// How the email looks in a client's iPhone Mail app: the inbox row, and the email opened.
// Drawn at real iPhone size (see IPhone.tsx) with Mail's own layout, so what Jen sees here
// is what her client sees. Words update as she types. {{first_name}} shows a sample name.

import type { ComponentChildren } from 'preact';
import './inbox.css';
import { IPhone } from './IPhone';
import { fillName, SAMPLE_NAME } from '../lib/preview';

const OTHERS = [
  { from: 'Mom', subject: 'Sunday dinner?', text: 'Bring the kids, I am making the lasagna you like.', time: '8:12 AM' },
  { from: 'Book Club', subject: "Next month's pick", text: 'We voted. Bring snacks and your opinions.', time: 'Yesterday' },
];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';

const Chevron = ({ dir = 'left' }: { dir?: 'left' | 'right' | 'up' | 'down' }) => {
  const d = { left: 'M10 2 3 9.5 10 17', right: 'M3 2l7 7.5L3 17', up: 'M2 10 9.5 3 17 10', down: 'M2 3l7.5 7L17 3' }[dir];
  const vertical = dir === 'up' || dir === 'down';
  return (
    <svg class={`m-chev m-chev-${dir}`} width={vertical ? 19 : 13} height={vertical ? 13 : 19} viewBox={vertical ? '0 0 19 13' : '0 0 13 19'} aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
};

function Avatar({ name }: { name: string }) {
  return <span class="m-av">{initials(name)}</span>;
}

function Row({ from, subject, text, time, unread }: { from: string; subject: string; text: string; time: string; unread?: boolean }) {
  return (
    <div class={`m-row${unread ? ' new' : ''}`}>
      <span class={`m-dot${unread ? '' : ' off'}`} />
      <Avatar name={from} />
      <div class="m-rbody">
        <div class="m-rtop">
          <b>{from}</b>
          <span class="m-time">
            {time}
            <Chevron dir="right" />
          </span>
        </div>
        <div class="m-subj">{subject}</div>
        <div class="m-pre">{text}</div>
      </div>
    </div>
  );
}

/** The inbox with her email on top. compact: a shorter crop for a narrow side column. */
export function InboxPreview({ fromName, subject, preheader, time = '9:41 AM', compact = false, width }: { fromName: string; subject: string; preheader: string; time?: string; compact?: boolean; width?: number }) {
  const from = fromName || 'Your spa';
  return (
    <div class="m-wrap" aria-label="How it looks in an iPhone inbox">
      <IPhone width={width ?? (compact ? 250 : 270)} crop={compact ? 330 : 560}>
        <div class="m-nav">
          <span class="m-link">
            <Chevron /> Mailboxes
          </span>
          <span class="m-link">Select</span>
        </div>
        <div class="m-large">Inbox</div>
        {!compact && (
          <div class="m-search">
            <svg width="17" height="17" viewBox="0 0 17 17" aria-hidden="true">
              <circle cx="7" cy="7" r="5.6" fill="none" stroke="currentColor" stroke-width="2" />
              <path d="m11.2 11.2 4.4 4.4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            Search
          </div>
        )}
        <Row
          key={from}
          unread
          from={from}
          time={time}
          subject={fillName(subject) || 'Your subject line'}
          text={fillName(preheader) || 'The preview line shows here. Use it to say one more thing.'}
        />
        {(compact ? OTHERS.slice(0, 1) : OTHERS).map((o) => (
          <Row key={o.from} from={o.from} subject={o.subject} text={o.text} time={o.time} />
        ))}
      </IPhone>
    </div>
  );
}

/** The email opened in Mail: Mail's header, then the email itself, then Mail's toolbar. */
export function MailMessage({ fromName, subject, width = 250, children }: { fromName: string; subject: string; width?: number; children: ComponentChildren }) {
  const from = fromName || 'Your spa';
  const line = { fill: 'none', stroke: 'currentColor', 'stroke-width': 1.9, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' } as const;
  return (
    <IPhone width={width}>
      <div class="m-nav">
        <span class="m-link">
          <Chevron /> Inbox
        </span>
        <span class="m-link m-updown">
          <Chevron dir="up" />
          <Chevron dir="down" />
        </span>
      </div>
      <div class="m-head">
        <Avatar name={from} />
        <div class="m-from">
          <b>{from}</b>
          <span>To: {SAMPLE_NAME}</span>
        </div>
        <span class="m-time">9:41 AM</span>
      </div>
      <div class="m-msubj">{fillName(subject) || 'Your subject line'}</div>
      <div class="m-body">{children}</div>
      <div class="m-tools" aria-hidden="true">
        <svg width="24" height="26" viewBox="0 0 24 26">
          <path {...line} d="M3 6h18M9 6V3.5h6V6M5.5 6l1.2 16.5h10.6L18.5 6M10 10.5v8M14 10.5v8" />
        </svg>
        <svg width="28" height="24" viewBox="0 0 28 24">
          <path {...line} d="M2.5 5.5V20a1.5 1.5 0 0 0 1.5 1.5h20a1.5 1.5 0 0 0 1.5-1.5V8.5A1.5 1.5 0 0 0 24 7H13l-2.5-3H4a1.5 1.5 0 0 0-1.5 1.5Z" />
        </svg>
        <svg width="26" height="22" viewBox="0 0 26 22">
          <path {...line} d="M10 3 2.5 10l7.5 7M3 10h12c5 0 8.5 3 8.5 9" />
        </svg>
        <svg width="25" height="25" viewBox="0 0 25 25">
          <path {...line} d="M11 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6M19 2.5l3.5 3.5L12 16.5l-4.5 1 1-4.5Z" />
        </svg>
      </div>
    </IPhone>
  );
}
